import React, { useState } from "react";
import { ExcelLayoutEditor } from "../shared/ExcelLayoutEditor";
import { ReportAccordionItem } from "../ReportAccordion";
import { ReportConfigSelect } from "../ReportConfigSelect";
import { reportToast } from "../../../../../components/notifications/appToast";
import {
    metricPositionWithinSheet,
    metricSheetName,
    moveExcelSheet,
    reorderExcelMetrics,
    reorderExcelSheets,
    defaultExcelSheetName,
    excelSheetKey,
    excelSheetsInUse,
    formatReportDate,
    isCustomReportingPeriod,
    renameExcelSheet,
    setMetricExcelSheet,
    today,
    validateExcelSheetName,
} from "../../reportConfigurationLogic";
import type {
    DataModel,
    MetricSection,
    PlanConfig,
    ReportConfig,
    ReportConfigurationLookupOption,
    WorkbookExcelHeader,
    WorkbookExcelLayout,
    WorkbookExcelSheetHeader,
} from "../../types";
import ctcReportLogo from "../../../../../assets/report-configuration/CallTheCar_logo.jpeg";
import type { ReportValidationIssue } from "../../validation/issues";

// Mounted by the page so editor drafts and drag state survive tab/report switches.
export function useWorkbookEditorState() {

    const [selectedExcelSheetKey, setSelectedExcelSheetKey] = useState("");
    const [excelSheetCreateOpen, setExcelSheetCreateOpen] = useState(false);
    const [newExcelSheetName, setNewExcelSheetName] = useState("");
    const [newExcelSheetMetricId, setNewExcelSheetMetricId] = useState("");
    const [excelSheetRenameKey, setExcelSheetRenameKey] = useState("");
    const [excelSheetRenameName, setExcelSheetRenameName] = useState("");
    const [excelSheetDeleteKey, setExcelSheetDeleteKey] = useState("");
    const [excelSheetDeleteTargetKey, setExcelSheetDeleteTargetKey] = useState("");
    const [draggedExcelSheetKey, setDraggedExcelSheetKey] = useState("");
    const [draggedExcelMetricId, setDraggedExcelMetricId] = useState("");
    const [excelSheetDropTarget, setExcelSheetDropTarget] = useState<{ key: string; placement: "before" | "after" } | null>(null);
    const [excelMetricDropTarget, setExcelMetricDropTarget] = useState<{ id: string; placement: "before" | "after" } | null>(null);
    return {
        selectedExcelSheetKey,
        setSelectedExcelSheetKey,
        excelSheetCreateOpen,
        setExcelSheetCreateOpen,
        newExcelSheetName,
        setNewExcelSheetName,
        newExcelSheetMetricId,
        setNewExcelSheetMetricId,
        excelSheetRenameKey,
        setExcelSheetRenameKey,
        excelSheetRenameName,
        setExcelSheetRenameName,
        excelSheetDeleteKey,
        setExcelSheetDeleteKey,
        excelSheetDeleteTargetKey,
        setExcelSheetDeleteTargetKey,
        draggedExcelSheetKey,
        setDraggedExcelSheetKey,
        draggedExcelMetricId,
        setDraggedExcelMetricId,
        excelSheetDropTarget,
        setExcelSheetDropTarget,
        excelMetricDropTarget,
        setExcelMetricDropTarget,
    };
}

type WorkbookLayoutEditorProps = {
    report: ReportConfig;
    session: ReturnType<typeof useWorkbookEditorState>;
    selectedPlan: PlanConfig;
    catalog: {
        reportingPeriodOptions: ReportConfigurationLookupOption[] | { id: string; value: string; name: string; }[];
        models: Record<string, DataModel>;
        dataModelIdForKey: (modelKey: string) => number | undefined;
        metricTemplateColumnId: (report: ReportConfig, section: MetricSection) => number | null;
        templateRefreshToken: number;
    };
    selection: {
        openExcelMetricId: string;
        setOpenExcelMetricId: React.Dispatch<React.SetStateAction<string>>;
    };
    editing: {
        replaceSelectedReport: (updater: (report: ReportConfig) => ReportConfig) => void;
        updateMetric: (metricId: string, updater: (metric: MetricSection) => MetricSection) => void;
    };
    validationIssues?: ReportValidationIssue[];
};

export function WorkbookLayoutEditor({ report, session, selectedPlan, catalog, selection, editing, validationIssues = [] }: WorkbookLayoutEditorProps) {
    const {
        selectedExcelSheetKey,
        excelSheetDeleteKey,
        newExcelSheetMetricId,
        excelSheetCreateOpen,
        newExcelSheetName,
        excelSheetRenameKey,
        excelSheetRenameName,
        setSelectedExcelSheetKey,
        setExcelSheetCreateOpen,
        setNewExcelSheetName,
        setNewExcelSheetMetricId,
        setExcelSheetRenameKey,
        setExcelSheetRenameName,
        excelSheetDeleteTargetKey,
        setExcelSheetDeleteKey,
        setExcelSheetDeleteTargetKey,
        draggedExcelSheetKey,
        setDraggedExcelSheetKey,
        setExcelSheetDropTarget,
        draggedExcelMetricId,
        setDraggedExcelMetricId,
        setExcelMetricDropTarget,
        excelSheetDropTarget,
        excelMetricDropTarget,
    } = session;
    const { reportingPeriodOptions, models, dataModelIdForKey, metricTemplateColumnId, templateRefreshToken } = catalog;
    const { openExcelMetricId, setOpenExcelMetricId } = selection;
    const { replaceSelectedReport, updateMetric } = editing;

    function renderExcelLayoutTab(report: ReportConfig) {
        if (!report.sections.length) return <section className="rc-block"><h3>Excel Layout</h3><p className="rc-hint">Add at least one Metric before configuring how it lays out on the Excel sheet.</p></section>;
        const sheets = excelSheetsInUse(report);
        const selectedSheet = sheets.find((sheet) => sheet.key === selectedExcelSheetKey) || sheets[0];
        const workbook = defaultWorkbookLayout(report);
        const parsedYear = new Date(report.effectiveStart || today).getFullYear();
        const reportYear = Number.isFinite(parsedYear) ? String(parsedYear) : String(new Date().getFullYear());
        const healthPlanName = selectedPlan.name || selectedPlan.clientName || "Health Plan";
        const reportName = report.label || "Report";
        const periodLabel = isCustomReportingPeriod(report.reportingPeriod.type)
            ? `${formatReportDate(report.reportingPeriod.customStart) || "Start"} - ${formatReportDate(report.reportingPeriod.customEnd) || "End"}`
            : reportingPeriodOptions.find((option) => option.value === report.reportingPeriod.type)?.name || report.reportingPeriod.type;
        const defaultTitleRowText = selectedSheet ? `${reportName} - ${selectedSheet.name} - ${periodLabel}` : reportName;
        const defaultHeaderLine1 = `${healthPlanName} Monthly Report for ${reportYear}`;
        const defaultHeaderLine2 = reportName;
        const defaultBrandedHeader: WorkbookExcelHeader = {
            enabled: false,
            showLogo: true,
            titleLine1: defaultHeaderLine1,
            titleLine2: defaultHeaderLine2,
            logoMode: "default",
            logoFileName: "CallTheCar_logo.jpeg",
        };
        const storedSheetHeader = selectedSheet ? workbook.sheetHeaders?.[selectedSheet.key] : undefined;
        const sheetHeader: WorkbookExcelSheetHeader = selectedSheet
            ? {
                enabled: false,
                titleTemplate: workbook.titleTemplate || defaultTitleRowText,
                header: selectedSheet.key === sheets[0]?.key ? (workbook.header || defaultBrandedHeader) : defaultBrandedHeader,
                ...storedSheetHeader,
            }
            : { enabled: false, titleTemplate: "", header: defaultBrandedHeader };
        const brandedHeader = sheetHeader.header || defaultBrandedHeader;
        const displayTitleRowText = resolveHeaderPreview(sheetHeader.titleTemplate);
        const displayHeaderLine1 = resolveBrandedHeaderText(brandedHeader.titleLine1);
        const displayHeaderLine2 = resolveBrandedHeaderText(brandedHeader.titleLine2);
        const sheetOptions = sheets.map((sheet) => ({ value: sheet.name, label: `${sheet.name} - ${sheet.enabledCount}/${sheet.totalCount} block${sheet.totalCount === 1 ? "" : "s"}` }));
        const deleteTargetOptions = sheets
            .filter((sheet) => sheet.key !== excelSheetDeleteKey)
            .map((sheet) => ({ value: sheet.key, label: `${sheet.name} - ${sheet.totalCount} block${sheet.totalCount === 1 ? "" : "s"}` }));
        const sheetForMetric = (sectionId: string) => sheets.find((sheet) => sheet.metrics.some((metric) => metric.section.id === sectionId));
        const canMoveMetricToNewSheet = (sectionId: string) => {
            const sourceSheet = sheetForMetric(sectionId);
            return !!sourceSheet && sourceSheet.totalCount > 1;
        };
        const firstMovableMetricId = report.sections.find((section) => canMoveMetricToNewSheet(section.id))?.id || "";
        const preferredMetricForNewSheet = newExcelSheetMetricId || openExcelMetricId || selectedSheet?.metrics[0]?.section.id || report.sections[0]?.id || "";
        const selectedMetricForNewSheet = canMoveMetricToNewSheet(preferredMetricForNewSheet) ? preferredMetricForNewSheet : firstMovableMetricId;
        const metricOptions = [
            ...(!firstMovableMetricId ? [{ value: "", label: "No movable metrics available", disabled: true }] : []),
            ...report.sections.map((section) => {
                const sourceSheet = sheetForMetric(section.id);
                const isMovable = canMoveMetricToNewSheet(section.id);
                return {
                    value: section.id,
                    label: `${section.name || "Untitled Metric"}${sourceSheet ? ` - ${sourceSheet.name}` : ""}${isMovable ? "" : " (only block on sheet)"}`,
                    disabled: !isMovable,
                };
            }),
        ];
        const createProblem = excelSheetCreateOpen ? validateExcelSheetName(newExcelSheetName) : null;
        const createDuplicate = excelSheetCreateOpen && sheets.some((sheet) => sheet.key === excelSheetKey(newExcelSheetName));
        const createAssignmentProblem = excelSheetCreateOpen && !selectedMetricForNewSheet
            ? "A new worksheet needs a metric that will not leave its current worksheet empty. Add another metric to the current worksheet, or rename the existing worksheet instead."
            : "";
        const renameProblem = excelSheetRenameKey ? validateExcelSheetName(excelSheetRenameName) : null;
        const renameDuplicate = excelSheetRenameKey && sheets.some((sheet) => sheet.key !== excelSheetRenameKey && sheet.key === excelSheetKey(excelSheetRenameName));
        const renameUnchanged = !!excelSheetRenameKey && excelSheetKey(excelSheetRenameName) === excelSheetRenameKey;

        function resolveHeaderPreview(template: string) {
            return (template || "")
                .replace(/\{ReportName\}/g, reportName)
                .replace(/\{SheetName\}/g, selectedSheet?.name || defaultExcelSheetName(report))
                .replace(/\{Period\}/g, periodLabel);
        }
        function resolveBrandedHeaderText(value: string) {
            return (value || "")
            .replace(/\{HealthPlan\}/g, healthPlanName)
            .replace(/\{ReportName\}/g, reportName)
            .replace(/\{Year\}/g, reportYear);
        }

        const setSheetHeader = (patch: Partial<WorkbookExcelSheetHeader>) => {
            if (!selectedSheet) return;
            updateWorkbookLayout(report, (current) => ({
                ...current,
                sheetHeaders: {
                    ...(current.sheetHeaders || {}),
                    [selectedSheet.key]: { ...sheetHeader, ...patch },
                },
            }));
        };
        const setBrandedHeader = (patch: Partial<WorkbookExcelHeader>) => {
            setSheetHeader({ header: { ...brandedHeader, ...patch } });
        };

        const createSheet = () => {
            const trimmed = newExcelSheetName.trim();
            if (validateExcelSheetName(trimmed) || createDuplicate || createAssignmentProblem || !selectedMetricForNewSheet) return;
            replaceSelectedReport((current) => setMetricExcelSheet(current, selectedMetricForNewSheet, trimmed));
            setSelectedExcelSheetKey(excelSheetKey(trimmed));
            setOpenExcelMetricId(selectedMetricForNewSheet);
            setExcelSheetCreateOpen(false);
            setNewExcelSheetName("");
            setNewExcelSheetMetricId("");
        };

        const commitRenameSheet = () => {
            const source = sheets.find((sheet) => sheet.key === excelSheetRenameKey);
            const trimmed = excelSheetRenameName.trim();
            if (!source || validateExcelSheetName(trimmed) || renameDuplicate) return;
            if (excelSheetKey(trimmed) === source.key) {
                setExcelSheetRenameKey("");
                setExcelSheetRenameName("");
                return;
            }
            replaceSelectedReport((current) => renameExcelSheet(current, source.name, trimmed));
            setSelectedExcelSheetKey(excelSheetKey(trimmed));
            setExcelSheetRenameKey("");
            setExcelSheetRenameName("");
        };

        const commitDeleteSheet = () => {
            const source = sheets.find((sheet) => sheet.key === excelSheetDeleteKey);
            const target = sheets.find((sheet) => sheet.key === excelSheetDeleteTargetKey);
            if (!source || !target || source.key === target.key) return;
            replaceSelectedReport((current) => moveExcelSheet(current, source.name, target.name));
            setSelectedExcelSheetKey(target.key);
            setExcelSheetDeleteKey("");
            setExcelSheetDeleteTargetKey("");
        };

        const startCreateSheet = () => {
            setExcelSheetCreateOpen(true);
            setNewExcelSheetMetricId(selectedMetricForNewSheet);
            const selectedMetric = report.sections.find((section) => section.id === selectedMetricForNewSheet) || report.sections[0];
            const modelLabel = selectedMetric?.model && models[selectedMetric.model]?.label ? models[selectedMetric.model].label : selectedMetric?.model;
            const seed = modelLabel ? `${modelLabel} - ` : "";
            setNewExcelSheetName(seed.length > 31 ? seed.slice(0, 31).trim() : seed);
        };

        const dropSheetOn = (targetKey: string, placement: "before" | "after") => {
            if (!draggedExcelSheetKey || draggedExcelSheetKey === targetKey) return;
            replaceSelectedReport((current) => reorderExcelSheets(current, draggedExcelSheetKey, targetKey, placement));
            setSelectedExcelSheetKey(draggedExcelSheetKey);
            setDraggedExcelSheetKey("");
            setExcelSheetDropTarget(null);
        };

        const dropMetricOn = (targetSectionId: string, placement: "before" | "after") => {
            if (!draggedExcelMetricId || draggedExcelMetricId === targetSectionId || !selectedSheet) return;
            replaceSelectedReport((current) => reorderExcelMetrics(current, selectedSheet.name, draggedExcelMetricId, targetSectionId, placement));
            setDraggedExcelMetricId("");
            setExcelMetricDropTarget(null);
        };

        const moveMetricToSheet = (sectionId: string, sheetName: string) => {
            const sourceSheet = sheetForMetric(sectionId);
            const targetKey = excelSheetKey(sheetName);
            if (sourceSheet && sourceSheet.key !== targetKey && sourceSheet.totalCount <= 1) {
                reportToast.warning(
                    "Worksheet would be removed",
                    "This metric is the only block on its current worksheet. Rename or delete the worksheet explicitly instead.",
                );
                return;
            }
            replaceSelectedReport((current) => setMetricExcelSheet(current, sectionId, sheetName));
        };

        return (
            <section className="rc-block rc-workbook-sheets">
                <div className="rc-workbook-sheets-head">
                    <div>
                        <h3>Excel Workbook</h3>
                        <p className="rc-block-hint">Arrange report metrics into workbook worksheets, then configure each block exactly where it will render.</p>
                    </div>
                    <button className="rc-action-button rc-action-secondary" type="button" onClick={startCreateSheet}>
                        <span className="material-icons md-18" aria-hidden="true">add</span>
                        <span>New Worksheet</span>
                    </button>
                </div>

                {excelSheetCreateOpen && (
                    <div className="rc-workbook-create-panel">
                        <div className="rc-workbook-create-title">
                            <span className="material-icons md-18" aria-hidden="true">post_add</span>
                            <div>
                                <strong>New worksheet</strong>
                                <span>Choose a metric from a worksheet that has multiple blocks. The sheet exists as soon as a metric points to it.</span>
                            </div>
                        </div>
                        <label className="rc-label-block">
                            Sheet name
                            <input value={newExcelSheetName} autoFocus onChange={(event) => setNewExcelSheetName(event.target.value)} />
                        </label>
                        <label className="rc-label-block">
                            Start with metric
                            <ReportConfigSelect<string>
                                value={selectedMetricForNewSheet}
                                options={metricOptions}
                                ariaLabel="Start new sheet with metric"
                                onChange={setNewExcelSheetMetricId}
                            />
                        </label>
                        <div className="rc-workbook-create-actions">
                            <button className="rc-iconbtn success" type="button" title="Create worksheet" aria-label="Create worksheet" disabled={!!createProblem || !!createDuplicate || !!createAssignmentProblem || !selectedMetricForNewSheet} onClick={createSheet}>
                                <span className="material-icons md-18" aria-hidden="true">check</span>
                            </button>
                            <button className="rc-iconbtn" type="button" title="Cancel" aria-label="Cancel new worksheet" onClick={() => { setExcelSheetCreateOpen(false); setNewExcelSheetName(""); }}>
                                <span className="material-icons md-18" aria-hidden="true">close</span>
                            </button>
                        </div>
                        {(createProblem || createDuplicate || createAssignmentProblem) && <span className="rc-hint">{createProblem || (createDuplicate ? "That sheet name is already in use." : createAssignmentProblem)}</span>}
                    </div>
                )}

                <div className="rc-workbook-shell">
                    <nav
                        className={`rc-workbook-sheet-rail${draggedExcelSheetKey ? " dragging-active" : ""}`}
                        aria-label="Workbook sheets"
                        onDragLeave={(event) => {
                            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setExcelSheetDropTarget(null);
                        }}
                    >
                        {sheets.map((sheet, index) => (
                            <button
                                key={sheet.key}
                                type="button"
                                className={[
                                    "rc-workbook-sheet-tab",
                                    selectedSheet?.key === sheet.key ? "active" : "",
                                    draggedExcelSheetKey === sheet.key ? "dragging" : "",
                                    excelSheetDropTarget?.key === sheet.key ? `drop-${excelSheetDropTarget.placement}` : "",
                                ].filter(Boolean).join(" ")}
                                draggable={sheets.length > 1}
                                title={`${sheet.name}\nDrag to reorder worksheet tabs`}
                                aria-label={`${sheet.name}, worksheet ${index + 1}. Drag to reorder.`}
                                onClick={() => setSelectedExcelSheetKey(sheet.key)}
                                onDragStart={(event) => {
                                    event.dataTransfer.effectAllowed = "move";
                                    event.dataTransfer.setData("text/plain", sheet.key);
                                    setDraggedExcelSheetKey(sheet.key);
                                }}
                                onDragOver={(event) => {
                                    if (!draggedExcelSheetKey || draggedExcelSheetKey === sheet.key) return;
                                    event.preventDefault();
                                    event.dataTransfer.dropEffect = "move";
                                    const bounds = event.currentTarget.getBoundingClientRect();
                                    const placement = event.clientX > bounds.left + bounds.width / 2 ? "after" : "before";
                                    setExcelSheetDropTarget((current) => current?.key === sheet.key && current.placement === placement ? current : { key: sheet.key, placement });
                                }}
                                onDrop={(event) => {
                                    event.preventDefault();
                                    const bounds = event.currentTarget.getBoundingClientRect();
                                    dropSheetOn(sheet.key, event.clientX > bounds.left + bounds.width / 2 ? "after" : "before");
                                }}
                                onDragEnd={() => { setDraggedExcelSheetKey(""); setExcelSheetDropTarget(null); }}
                            >
                                <span className="rc-workbook-sheet-tab-index">{index + 1}</span>
                                <span className="rc-workbook-sheet-tab-main">
                                    <strong>{sheet.name}</strong>
                                    <small>{sheet.enabledCount}/{sheet.totalCount} blocks</small>
                                </span>
                                <span className="material-icons md-16 rc-workbook-drag-glyph" aria-hidden="true">drag_indicator</span>
                            </button>
                        ))}
                    </nav>

                    {selectedSheet && (
                        <div className="rc-workbook-sheet-canvas">
                            <div className="rc-workbook-sheet-toolbar">
                                <div>
                                    <span className="rc-workbook-sheet-kicker">Worksheet</span>
                                    <h4>{selectedSheet.name}</h4>
                                    <p>{selectedSheet.enabledCount} included block{selectedSheet.enabledCount === 1 ? "" : "s"} will render on this sheet.</p>
                                </div>
                                <div className="rc-workbook-sheet-actions">
                                    {excelSheetRenameKey === selectedSheet.key ? (
                                        <div className="rc-workbook-rename-editor">
                                            <div className="rc-workbook-rename-input">
                                                <input
                                                    value={excelSheetRenameName}
                                                    autoFocus
                                                    aria-label={`Rename ${selectedSheet.name}`}
                                                    onChange={(event) => setExcelSheetRenameName(event.target.value)}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter") commitRenameSheet();
                                                        if (event.key === "Escape") {
                                                            setExcelSheetRenameKey("");
                                                            setExcelSheetRenameName("");
                                                        }
                                                    }}
                                                />
                                                <span>{selectedSheet.totalCount} affected</span>
                                            </div>
                                            <button className="rc-iconbtn success" type="button" title="Save sheet name" aria-label="Save sheet name" disabled={!!renameProblem || !!renameDuplicate || renameUnchanged} onClick={commitRenameSheet}>
                                                <span className="material-icons md-18" aria-hidden="true">check</span>
                                            </button>
                                            <button className="rc-iconbtn" type="button" title="Cancel rename" aria-label="Cancel rename" onClick={() => { setExcelSheetRenameKey(""); setExcelSheetRenameName(""); }}>
                                                <span className="material-icons md-18" aria-hidden="true">close</span>
                                            </button>
                                        </div>
                                    ) : (
                                        <button className="rc-iconbtn" type="button" title="Rename worksheet" aria-label={`Rename ${selectedSheet.name}`} onClick={() => { setExcelSheetRenameKey(selectedSheet.key); setExcelSheetRenameName(selectedSheet.name); }}>
                                            <span className="material-icons md-18" aria-hidden="true">drive_file_rename_outline</span>
                                        </button>
                                    )}
                                    <button className="rc-iconbtn danger" type="button" title="Delete worksheet" aria-label={`Delete ${selectedSheet.name}`} disabled={sheets.length <= 1} onClick={() => {
                                        setExcelSheetDeleteKey(selectedSheet.key);
                                        setExcelSheetDeleteTargetKey(sheets.find((sheet) => sheet.key !== selectedSheet.key)?.key || "");
                                    }}>
                                        <span className="material-icons md-18" aria-hidden="true">delete_outline</span>
                                    </button>
                                </div>
                                {(renameProblem || renameDuplicate) && <span className="rc-hint">{renameProblem || "That sheet name is already in use."}</span>}
                            </div>

                            {excelSheetDeleteKey === selectedSheet.key && (
                                <div className="rc-workbook-sheet-action warning">
                                    <span>{selectedSheet.totalCount} metric{selectedSheet.totalCount === 1 ? "" : "s"} will move before "{selectedSheet.name}" is removed.</span>
                                    <ReportConfigSelect<string>
                                        value={excelSheetDeleteTargetKey || deleteTargetOptions[0]?.value || ""}
                                        options={deleteTargetOptions}
                                        ariaLabel="Move metrics to sheet before deleting"
                                        disabled={!deleteTargetOptions.length}
                                        onChange={setExcelSheetDeleteTargetKey}
                                    />
                                    <button className="rc-iconbtn danger" type="button" title="Move metrics and delete worksheet" aria-label="Move metrics and delete worksheet" disabled={!excelSheetDeleteTargetKey} onClick={commitDeleteSheet}>
                                        <span className="material-icons md-18" aria-hidden="true">check</span>
                                    </button>
                                    <button className="rc-iconbtn" type="button" title="Cancel delete" aria-label="Cancel delete" onClick={() => setExcelSheetDeleteKey("")}>
                                        <span className="material-icons md-18" aria-hidden="true">close</span>
                                    </button>
                                </div>
                            )}

                            <div className="rc-workbook-sheet-header-card">
                                <div className="rc-workbook-sheet-header-title">
                                    <div>
                                        <h5>Sheet output header</h5>
                                        <p className="rc-hint">Controls only the header rows rendered on "{selectedSheet.name}".</p>
                                    </div>
                                </div>
                                <div className="rc-workbook-sheet-header-grid">
                                    <div className="rc-workbook-sheet-header-option">
                                        <div className="rc-workbook-option-head">
                                            <div>
                                                <strong>Title row</strong>
                                                <span>Single row above the sheet blocks.</span>
                                            </div>
                                            <label className="rc-switch">
                                                <input type="checkbox" checked={sheetHeader.enabled} onChange={(event) => setSheetHeader({ enabled: event.target.checked, titleTemplate: event.target.checked && !sheetHeader.titleTemplate ? defaultTitleRowText : sheetHeader.titleTemplate })} />
                                                <span aria-hidden="true" />
                                            </label>
                                        </div>
                                        {sheetHeader.enabled && (
                                            <div className="rc-workbook-sheet-header-fields">
                                                <input
                                                    value={displayTitleRowText}
                                                    onChange={(event) => setSheetHeader({ titleTemplate: event.target.value })}
                                                    placeholder={defaultTitleRowText}
                                                    aria-label="Title row text"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="rc-workbook-sheet-header-option">
                                        <div className="rc-workbook-option-head">
                                            <div>
                                                <strong>Branded header</strong>
                                                <span>Report title and optional logo on this sheet.</span>
                                            </div>
                                            <label className="rc-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={brandedHeader.enabled}
                                                    onChange={(event) => setBrandedHeader({
                                                        enabled: event.target.checked,
                                                        titleLine1: event.target.checked && !brandedHeader.titleLine1 ? defaultHeaderLine1 : brandedHeader.titleLine1,
                                                        titleLine2: event.target.checked && !brandedHeader.titleLine2 ? defaultHeaderLine2 : brandedHeader.titleLine2,
                                                    })}
                                                />
                                                <span aria-hidden="true" />
                                            </label>
                                        </div>
                                        {brandedHeader.enabled && (
                                            <div className="rc-workbook-sheet-header-fields">
                                                <div className="rc-workbook-header-mini-grid">
                                                    <label className="rc-label-block">
                                                        Header
                                                        <input value={displayHeaderLine1} onChange={(event) => setBrandedHeader({ titleLine1: event.target.value })} placeholder={defaultHeaderLine1} />
                                                    </label>
                                                    <label className="rc-label-block">
                                                        Subtitle
                                                        <input value={displayHeaderLine2} onChange={(event) => setBrandedHeader({ titleLine2: event.target.value })} placeholder={defaultHeaderLine2} />
                                                    </label>
                                                </div>
                                                <label className="rc-checkbox-row rc-excel-enable compact">
                                                    <input type="checkbox" checked={brandedHeader.showLogo} onChange={(event) => setBrandedHeader({ showLogo: event.target.checked })} />
                                                    <span className="rc-excel-check-box" aria-hidden="true" />
                                                    <span>Show CallTheCar logo</span>
                                                </label>
                                                <div className="rc-workbook-preview-surface compact">
                                                    <div className="rc-workbook-preview-copy">
                                                        <strong>{displayHeaderLine1}</strong>
                                                        <span>{displayHeaderLine2}</span>
                                                    </div>
                                                    {brandedHeader.showLogo && (
                                                        <div className="rc-workbook-logo-preview compact" aria-label="Default CallTheCar logo">
                                                            <img src={ctcReportLogo} alt="CallTheCar" />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div
                                className={`rc-workbook-block-list${draggedExcelMetricId ? " dragging-active" : ""}`}
                                onDragLeave={(event) => {
                                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setExcelMetricDropTarget(null);
                                }}
                            >
                                {selectedSheet.metrics.map(({ section }, metricIndex) => {
                                    const layout = section.excelLayout;
                                    const enabled = !!layout?.enabled;
                                    const isDetailMode = section.displayAs === "detail";
                                    const columnCount = isDetailMode
                                        ? (layout?.columns || []).filter((column) => column.detail?.include ?? true).length
                                        : layout?.columns.length || 0;
                                    const columnCountLabel = isDetailMode
                                        ? `${columnCount || "No"} column${columnCount === 1 ? "" : "s"} on this sheet`
                                        : `${columnCount || "No"} workbook column${columnCount === 1 ? "" : "s"} configured`;
                                    const dataModelId = section.model ? dataModelIdForKey(section.model) : undefined;
                                    const dataModelColumnId = metricTemplateColumnId(report, section) || undefined;
                                    const position = metricPositionWithinSheet(report, section.id);
                                    return (
                                        <div
                                            key={section.id}
                                            className={[
                                                "rc-workbook-block-shell",
                                                draggedExcelMetricId === section.id ? "dragging" : "",
                                                excelMetricDropTarget?.id === section.id ? `drop-${excelMetricDropTarget.placement}` : "",
                                            ].filter(Boolean).join(" ")}
                                            onDragOver={(event) => {
                                                if (!draggedExcelMetricId || draggedExcelMetricId === section.id) return;
                                                event.preventDefault();
                                                event.dataTransfer.dropEffect = "move";
                                                const bounds = event.currentTarget.getBoundingClientRect();
                                                const placement = event.clientY > bounds.top + bounds.height / 2 ? "after" : "before";
                                                setExcelMetricDropTarget((current) => current?.id === section.id && current.placement === placement ? current : { id: section.id, placement });
                                            }}
                                            onDrop={(event) => {
                                                event.preventDefault();
                                                const bounds = event.currentTarget.getBoundingClientRect();
                                                dropMetricOn(section.id, event.clientY > bounds.top + bounds.height / 2 ? "after" : "before");
                                            }}
                                        >
                                            <button
                                                className="rc-workbook-block-drag"
                                                type="button"
                                                draggable
                                                title="Drag to reorder this block on the current worksheet"
                                                aria-label={`Drag ${section.name || "metric"} to reorder`}
                                                onDragStart={(event) => {
                                                    event.dataTransfer.effectAllowed = "move";
                                                    event.dataTransfer.setData("text/plain", section.id);
                                                    setDraggedExcelMetricId(section.id);
                                                }}
                                                onDragEnd={() => { setDraggedExcelMetricId(""); setExcelMetricDropTarget(null); }}
                                            >
                                                <span className="material-icons md-18" aria-hidden="true">drag_indicator</span>
                                            </button>
                                            <ReportAccordionItem
                                                id={`excel-metric-${section.id}`}
                                                eyebrow={`${selectedSheet.name} / Block ${metricIndex + 1}`}
                                                title={`${position ? `#${position.position} ` : ""}${layout?.title || section.name || "Untitled Metric"}`}
                                                summary={`${metricSheetName(report, section)} - ${columnCountLabel}`}
                                                meta={<span className={enabled ? "rc-badge strong" : "rc-badge"}>{enabled ? "Included" : "Not included"}</span>}
                                                open={openExcelMetricId === section.id}
                                                onOpenChange={(nextOpen) => {
                                                    setOpenExcelMetricId(nextOpen ? section.id : "");
                                                }}
                                            >
                                                <div className="rc-workbook-block-tools">
                                                    <label className="rc-label-block">
                                                        Move to sheet
                                                        <ReportConfigSelect<string>
                                                            value={metricSheetName(report, section)}
                                                            options={sheetOptions}
                                                            ariaLabel={`Move ${section.name || "metric"} to sheet`}
                                                            onChange={(value) => moveMetricToSheet(section.id, value)}
                                                        />
                                                    </label>
                                                </div>
                                                {openExcelMetricId === section.id && (
                                                    <ExcelLayoutEditor
                                                        report={report}
                                                        section={section}
                                                        onChange={(updater) => updateMetric(section.id, updater)}
                                                        dataModelId={dataModelId}
                                                        dataModelColumnId={dataModelColumnId}
                                                        templateRefreshToken={templateRefreshToken}
                                                        dataModels={models}
                                                        validationIssues={validationIssues}
                                                    />
                                                )}
                                            </ReportAccordionItem>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </section>
        );
    }

    function defaultWorkbookLayout(report: ReportConfig): WorkbookExcelLayout {
        return {
            titleTemplate: report.excelWorkbook?.titleTemplate || "",
            sheetHeaders: report.excelWorkbook?.sheetHeaders || {},
            sheetOrder: report.excelWorkbook?.sheetOrder || [],
            header: {
                enabled: !!report.excelWorkbook?.header?.enabled,
                showLogo: report.excelWorkbook?.header?.showLogo ?? true,
                titleLine1: report.excelWorkbook?.header?.titleLine1 || "",
                titleLine2: report.excelWorkbook?.header?.titleLine2 || "",
                logoMode: "default",
                logoFileName: report.excelWorkbook?.header?.logoFileName || "CallTheCar_logo.jpeg",
            },
        };
    }

    function updateWorkbookLayout(report: ReportConfig, updater: (layout: WorkbookExcelLayout) => WorkbookExcelLayout) {
        replaceSelectedReport((current) => ({ ...current, excelWorkbook: updater(defaultWorkbookLayout(current)) }));
    }
    return renderExcelLayoutTab(report);
}
